import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import { useOrg } from '@/contexts/OrgContext';
import {
  useSetUserPreference,
  useUserPreferences,
} from '@/hooks/useUserPreferences';
import { logger } from '@/lib/logger';
import { PREF_THEME } from '@/types/userPreferences';

export type Theme = 'light' | 'dark' | 'system';

const THEME_VALUES: readonly Theme[] = ['light', 'dark', 'system'];
const THEME_SCOPE = '';

function coerceTheme(value: unknown): Theme | undefined {
  return typeof value === 'string' &&
    (THEME_VALUES as readonly string[]).includes(value)
    ? (value as Theme)
    : undefined;
}

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Get theme from localStorage or default to system
    try {
      return coerceTheme(localStorage.getItem('theme')) ?? 'system';
    } catch (err) {
      logger.warn('Failed to load theme from localStorage', {
        error: err,
        component: 'ThemeContext',
      });
      return 'system';
    }
  });

  const [isDark, setIsDark] = useState(false);

  const syncTheme = useCallback(() => {
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches;
    const shouldBeDark =
      theme === 'dark' || (theme === 'system' && prefersDark);

    setIsDark(shouldBeDark);

    // Apply theme to document
    // Note: We add both 'dark' (for design-system.css) and 'dark-mode' (for theme.css/Untitled UI)
    if (shouldBeDark) {
      document.documentElement.classList.add('dark', 'dark-mode');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark', 'dark-mode');
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, [theme]);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme);
      try {
        localStorage.setItem('theme', newTheme);
      } catch (err) {
        logger.warn('Failed to persist theme preference', {
          error: err,
          component: 'ThemeContext',
        });
      }
      syncTheme();
    },
    [syncTheme]
  );

  useEffect(() => {
    syncTheme();

    // Watch for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        syncTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    // Watch for localStorage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'theme') {
        const newTheme = coerceTheme(e.newValue) ?? 'system';
        setThemeState(newTheme);
        syncTheme();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [theme, syncTheme]);

  const value: ThemeContextType = useMemo(
    () => ({
      theme,
      setTheme,
      isDark,
    }),
    [theme, setTheme, isDark]
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
};

export function ThemePreferenceSync() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  if (!orgId) {
    // no org (logged-out/booking/admin) -> behave exactly as today
    return null;
  }
  return <ThemePreferenceSyncInner key={orgId} orgId={orgId} />;
}

function ThemePreferenceSyncInner({ orgId }: { orgId: number }) {
  const { theme, setTheme } = useTheme();
  const { getPreference, isSuccess } = useUserPreferences(orgId);
  const setPreference = useSetUserPreference(orgId);
  const serverTheme = coerceTheme(
    getPreference<string>(PREF_THEME, THEME_SCOPE)
  );
  const hasReconciledRef = useRef(false);
  const lastSyncedRef = useRef<Theme | null>(null);

  const reconcileOnce = useEffectEvent(() => {
    // server wins once. Set lastSyncedRef BEFORE setTheme so the persist
    // effect below sees the value already synced and does not write it back.
    lastSyncedRef.current = serverTheme ?? theme;
    if (serverTheme && serverTheme !== theme) {
      setTheme(serverTheme);
    }
    hasReconciledRef.current = true;
    // Absent/invalid server value: adopt local, do NOT proactively write a seed.
    // The first user change persists via the persist effect below.
  });

  const persistIfChanged = useEffectEvent(() => {
    if (theme !== lastSyncedRef.current) {
      lastSyncedRef.current = theme; // set before mutate -> idempotent under refetch/multi-tab
      setPreference.mutate({
        key: PREF_THEME,
        scope: THEME_SCOPE,
        value: theme,
      });
    }
  });

  // (1) reconcile exactly once, when the preference query first settles.
  useEffect(() => {
    if (isSuccess && !hasReconciledRef.current) {
      reconcileOnce();
    }
    // An Effect Event is never a dependency (REACT_19_PATTERNS § 4.1); the
    // installed react-hooks plugin predates useEffectEvent and can't tell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  // (2) persist subsequent LOCAL theme changes only.
  useEffect(() => {
    if (hasReconciledRef.current) {
      persistIfChanged();
    }
    // An Effect Event is never a dependency (REACT_19_PATTERNS § 4.1); the
    // installed react-hooks plugin predates useEffectEvent and can't tell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  return null;
}
